import { Autocomplete, AutocompleteItem } from '@heroui/react';
import React, { useState } from 'react';

import { useWorkspaceDirectory } from '../../hooks';
import { memberName } from '../../utils';

export interface ViewAsPickerProps {
  workspaceId: string;
  /** The member whose view is shown. Null shows the whole workspace. */
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
}

/**
 * Picks the member whose view of the workspace to show. The list comes from the
 * directory, so anyone in the workspace can be chosen, whether or not they share
 * a location with you.
 */
export const ViewAsPicker: React.FC<ViewAsPickerProps> = ({
  workspaceId,
  selectedUserId,
  onSelect,
}) => {
  // The name of the current choice, which is what the closed box reads.
  const [chosenName, setChosenName] = useState('');
  // What is typed while the list is open. Opening starts it empty so the whole
  // workspace is offered again rather than only the name already chosen.
  const [typed, setTyped] = useState('');
  const [isOpen, setOpen] = useState(false);

  const { members, isLoading } = useWorkspaceDirectory(
    workspaceId,
    { searchTerm: isOpen ? typed : '' },
    10
  );

  const pick = (key: React.Key | null) => {
    const userId = key ? String(key) : null;
    const picked = members.find((member) => member.userId === userId);
    setChosenName(picked ? memberName(picked.name) : '');
    onSelect(userId);
  };

  const clear = () => {
    setChosenName('');
    setTyped('');
    onSelect(null);
  };

  return (
    <Autocomplete
      label="Show what one person sees"
      placeholder="Everyone in the workspace"
      description="Pick someone to see only the people they work alongside. Clear it to see everyone."
      variant="bordered"
      size="sm"
      className="max-w-md"
      isLoading={isLoading}
      // Open on the whole list; closed, read back the current choice.
      inputValue={isOpen ? typed : chosenName}
      onInputChange={setTyped}
      onOpenChange={(open) => {
        setOpen(open);
        if (open) {
          setTyped('');
        }
      }}
      selectedKey={selectedUserId}
      onSelectionChange={pick}
      allowsCustomValue={false}
      isClearable
      onClear={clear}
    >
      {members.map((member) => (
        <AutocompleteItem
          key={member.userId}
          textValue={memberName(member.name)}
          // One person at a time, so the tick beside the current choice reads
          // as a multi-select control it is not.
          hideSelectedIcon
        >
          <div className="flex flex-col">
            <span className="text-small">{memberName(member.name)}</span>
            {member.email && (
              <span className="text-tiny text-default-400">{member.email}</span>
            )}
          </div>
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
};
